import { Button, Image, Popover } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../../../assets/styles/custom/components/messaging_header.scss';
import { ReactComponent as UserLeave } from '../../../../assets/svg/icons/leave.svg';
import { ReactComponent as MenuSvg } from '../../../../assets/svg/icons/menu.svg';
import { copyLink, getCurrentColor, getLinkCopyPopup, onUserImgError } from '../../../../helpers/Helpers';
import { CLOSE_ACTION_MODAL_CHILD, OPEN_ACTION_MODAL_CHILD } from '../../../../redux/actions/componentsActions';
import { SHOW_CHAT_MEMBERS } from '../../../../redux/actions/currentChatActions';
import { LEAVE_CHAT } from '../../../../redux/actions/dialogsActions';

const MessagingHeader = ({ currentChat }) => {
    const dispatch = useDispatch();

    const account = useSelector((state) => state.account);
    const notificationMethod = useSelector((state) => state.notificationMethodPopup);
    const { groupUniversityId } = useSelector((state) => state.widget);

    const [visiblePopoverMenu, setVisiblePopoverMenu] = useState(false);
    const [shareButton, setShareButton] = useState(null);

    const handleLeaveChatClick = useCallback(
        (e) => {
            e.stopPropagation();
            dispatch({
                type: OPEN_ACTION_MODAL_CHILD,
                payload: {
                    children: (
                        <div className="general-popup leave-chat-popup" id="verification">
                            <h2 className="modal-title">Leave group chat?</h2>
                            <div className="sub-title">
                                If you've finished chatting you can leave this group chat and you will stop receiving
                                notifications
                            </div>
                            <div className="btn-wrap confirm-ban-buttons">
                                <Button type="submit" className="el-btn general-action-button" onClick={leaveChat}>
                                    Leave
                                </Button>
                                <Button
                                    type="submit"
                                    className="el-btn general-action-button cancel"
                                    onClick={handleClosePopup}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ),
                },
            });
        },
        [dispatch]
    );

    const handleClosePopup = useCallback(() => {
        dispatch({
            type: CLOSE_ACTION_MODAL_CHILD,
        });
    }, [dispatch]);

    const shareLink = useCallback(
        (e) => {
            e.stopPropagation();
            setTimeout(() => {
                setVisiblePopoverMenu(false);
            }, 4000);
            copyLink('chat', currentChat.data.id, groupUniversityId || currentChat.university.id);
            shareButton.focus();
        },
        [currentChat, groupUniversityId, shareButton]
    );

    const showMembersModal = useCallback(() => {
        dispatch({
            type: SHOW_CHAT_MEMBERS,
            payload: {
                showMembers: true,
            },
        });
        setVisiblePopoverMenu(false);
    }, [dispatch]);

    const leaveChat = useCallback(() => {
        const dialogId = currentChat.activeChat;
        dispatch({
            type: LEAVE_CHAT,
            payload: {
                dialogId,
                groupDialogs: true,
            },
        });
    }, [dispatch, currentChat]);

    const linkCopyPopup = useMemo(() => getLinkCopyPopup(currentChat?.data?.id), [currentChat]);

    const currentChatInfo = currentChat.data;
    const membersView = [];
    let isMember = false;
    if (currentChatInfo.members) {
        let usersCount = 0;
        for (const member of currentChatInfo.members) {
            if (
                (!account.account_info || (account.account_info && account.account_info.id !== member.id)) &&
                usersCount < 2 &&
                member.role_key === 'univercity-student'
            ) {
                usersCount += 1;
                membersView.push(
                    member.avatar ? (
                        <div key={member.id} className="chat-random-img-block">
                            <Image
                                className="chat-name-img"
                                src={member.avatar.sizes['70x70'] || member.avatar.original}
                                onError={onUserImgError}
                                alt={member.name ? member.name.slice(0, 1).toUpperCase() : 'D'}
                            />
                        </div>
                    ) : (
                        <div
                            key={member.id}
                            className={
                                'chat-random-img-block' +
                                (account.account_info && account.account_info.id == member.id
                                    ? ' personal-random-avatar'
                                    : '')
                            }
                            style={{ backgroundColor: getCurrentColor(member) }}
                        >
                            <span>{member.name ? member.name.slice(0, 1).toUpperCase() : 'D'}</span>
                        </div>
                    )
                );
            } else if (account.account_info && account.account_info.id === member.id) {
                isMember = true;
            }
        }
    }

    return (
        <div className="chat-head group-chat-header">
            <div className="chat-head-left" onClick={showMembersModal}>
                {membersView.length > 0 && <div className="chat-header-members-view">{membersView}</div>}
                <div className="chat-header-text-info">
                    <div className="current-chat-name">{currentChat.data.name}</div>
                    <div className="current-chat-counters">
                        {currentChatInfo.ambassadors && currentChatInfo.ambassadors.length + ' Ambassadors, '}
                        {currentChatInfo.prospects && currentChatInfo.prospects.length + ' Participants'}
                    </div>
                </div>
            </div>
            <div className="chat-head-right">
                {
                    <Popover
                        overlayClassName="group-chat-options-popover"
                        trigger="click"
                        placement="bottomRight"
                        open={visiblePopoverMenu}
                        onOpenChange={(visible) => setVisiblePopoverMenu(visible)}
                        content={
                            <div className="group-chat-options-popover-container">
                                <Button
                                    className="group-chat-options-popover-option"
                                    aria-label="List of users"
                                    onClick={showMembersModal}
                                >
                                    <div className="option-text">List of users</div>
                                </Button>
                                {!currentChatInfo.is_private && (
                                    <Button
                                        ref={(button) => setShareButton(button)}
                                        className="group-chat-options-popover-option"
                                        aria-label="Share link"
                                        onClick={(e) => shareLink(e)}
                                    >
                                        <div className="option-text">Share link</div>
                                        {linkCopyPopup}
                                    </Button>
                                )}
                            </div>
                        }
                    >
                        <MenuSvg />
                    </Popover>
                }
                {isMember && account.account_info && account.account_info.key === 'prospect' && (
                    <Button
                        htmlType="button"
                        className="chat-control leave-conversation"
                        aria-label="Leave conversation"
                        title="Leave"
                        onClick={handleLeaveChatClick}
                    >
                        <UserLeave />
                        <span className="toggle-left">Leave conversation</span>
                    </Button>
                )}
            </div>
        </div>
    );
};

export default MessagingHeader;
